/**
 * feat-338 — Section 4, AI "Suggest edits" (design 03, NOTES §7).
 *
 * POSTs `/questions/{id}/suggest` with the page's filters. The backend recomputes the
 * stats itself and never sees a learner identifier. Nothing is stored and nothing
 * edits the curriculum: Copy puts plain text on the clipboard for a human.
 *
 * States: idle → loading → result | no change | error (502/network: danger banner, any
 * previous result CLEARED) | rate limited (429: neutral notice with the server detail)
 * | not enough learners (422: neutral notice).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Clock, Copy, Info, Loader2, Sparkles } from 'lucide-react';
import {
  QuestionAnalysisFilters,
  QuestionContent,
  SuggestResponse,
  suggestEdits,
} from '../../../../services/questionAnalysisService';
import { C, DEFAULT_TIMEZONE, ISSUE_LABEL, TYPE_LABEL } from './questionAnalysisFormat';
import { ErrorBanner, httpStatusOf } from './parts';

export const SUGGEST_COPY = {
  button: 'Suggest edits',
  again: 'Suggest again',
  loadingButton: 'Suggesting…',
  title: 'Suggested edits',
  caption: 'Written by AI from this question’s results. Not saved, and nothing changes until someone edits the Learn Module.',
  loadingBody: 'Reading the question and its results. This usually takes 10–20 seconds.',
  copy: 'Copy',
  copied: 'Copied',
  copyHint: 'Copy puts the question and these suggestions on your clipboard as plain text, ready to paste into a message.',
  noChangeTitle: 'No change recommended',
  now: 'now',
  suggested: 'suggested',
  noChange: 'No change',
  rowQuestion: 'Question',
  rowOptions: 'Options',
  rowAccepted: 'Accepted answers',
  rowExplanation: 'Explanation',
  correct: 'correct',
  error: 'Couldn’t generate suggestions, try again.',
  errorDetail: 'Nothing was changed.',
  tryAgain: 'Try again',
  limitedTitle: 'You’ve asked for a lot of suggestions in a short time.',
  limitedFallback: 'Wait a minute, then try again.',
  notEnoughFallback: 'Not enough learners to analyse this question.',
} as const;

export const COPIED_MS = 2000;

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'result'; data: SuggestResponse }
  | { kind: 'error' }
  | { kind: 'limited'; detail: string | null }
  | { kind: 'notice'; detail: string | null };

function detailOf(err: unknown): string | null {
  const body = (err as { body?: unknown } | null)?.body;
  const d = (body as { detail?: unknown } | null)?.detail;
  return typeof d === 'string' && d.trim() ? d : null;
}

function letter(i: number) {
  return String.fromCharCode(65 + i);
}

/**
 * True when the suggestion keeps the options but moves the correct answer
 * (`suggested_options` null, `suggested_correct_index` set and different). That IS a
 * change, and often the one that matters (a wrong or risky marked answer).
 */
export function isAnswerKeyOnlyChange(q: QuestionContent, s: SuggestResponse): boolean {
  return s.suggested_options == null && s.suggested_correct_index != null && s.suggested_correct_index !== q.correct_index;
}

/** Plain text for Matt: current vs suggested, issues, summary, provenance. */
export function buildCopyText(q: QuestionContent, s: SuggestResponse, now: Date = new Date(), timeZone = DEFAULT_TIMEZONE): string {
  const isFitb = q.question_type === 'fill_blank';
  const lines: string[] = [];
  lines.push(`Question (${q.drill_name} · ${q.lesson_name}), ${TYPE_LABEL[q.question_type].toLowerCase()}`);
  lines.push(`Now: ${q.question_text}`);
  if (s.no_change_recommended) {
    lines.push('Suggested: no change recommended');
  } else {
    lines.push(`Suggested: ${s.suggested_question_text ?? 'no change'}`);
    if (isFitb) {
      lines.push(`Accepted answers now: ${(q.accepted_answers ?? []).join(', ') || '(none)'}`);
      lines.push(`Suggested accepted answers: ${s.suggested_accepted_answers ? s.suggested_accepted_answers.join(', ') : 'no change'}`);
    } else {
      const fmt = (opts: string[], correct: number | null) =>
        opts.map((o, i) => `${letter(i)}) ${o}${i === correct ? ' (correct)' : ''}`).join('; ');
      lines.push(`Options now: ${fmt(q.options ?? [], q.correct_index)}`);
      let sugg: string;
      if (s.suggested_options) {
        sugg = fmt(s.suggested_options, s.suggested_correct_index ?? q.correct_index);
      } else if (isAnswerKeyOnlyChange(q, s)) {
        const i = s.suggested_correct_index as number;
        const text = (q.options ?? [])[i];
        sugg = `same options, correct answer now ${letter(i)})${text != null ? ` ${text}` : ''}`;
      } else {
        sugg = 'no change';
      }
      lines.push(`Suggested options: ${sugg}`);
    }
    const currentExpl = q.explanation || q.correct_explanation;
    if (s.suggested_explanation == null) {
      lines.push('Explanation: no change');
    } else {
      lines.push(`Explanation now: ${currentExpl ?? '(none)'}`);
      lines.push(`Suggested explanation: ${s.suggested_explanation}`);
    }
  }
  if (s.issues.length) {
    lines.push(`Issues: ${s.issues.map((i) => `${ISSUE_LABEL[i.category] ?? i.category}: ${i.explanation}`).join('; ')}`);
  }
  lines.push(`Summary: ${s.summary}`);
  let date: string;
  try {
    date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone });
  } catch {
    date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  lines.push(`(AI suggestion from Bespoke Question Analysis, ${date}. Not applied.)`);
  return lines.join('\n');
}

export function useSuggestEdits(tenantId: string, questionId: string, filters: QuestionAnalysisFilters) {
  const [state, setState] = useState<State>({ kind: 'idle' });
  const seq = useRef(0);
  const filtersKey = JSON.stringify(filters);

  // A different question or filter context invalidates any suggestion on screen.
  useEffect(() => {
    seq.current++;
    setState({ kind: 'idle' });
  }, [tenantId, questionId, filtersKey]);

  const run = useCallback(() => {
    const my = ++seq.current;
    // Clear any previous result: never show stale or partial content.
    setState({ kind: 'loading' });
    suggestEdits(tenantId, questionId, JSON.parse(filtersKey)).then(
      (data) => {
        if (my === seq.current) setState({ kind: 'result', data });
      },
      (err) => {
        if (my !== seq.current) return;
        const status = httpStatusOf(err);
        if (status === 429) setState({ kind: 'limited', detail: detailOf(err) });
        else if (status === 422) setState({ kind: 'notice', detail: detailOf(err) });
        else setState({ kind: 'error' });
      },
    );
  }, [tenantId, questionId, filtersKey]);

  return { state, run };
}

export const SuggestButton: React.FC<{ state: State; onClick: () => void }> = ({ state, onClick }) => {
  const loading = state.kind === 'loading';
  return (
    <button type="button" className={C.btnInk} onClick={onClick} disabled={loading} data-testid="suggest-button">
      {loading ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
      ) : (
        <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
      )}
      {loading ? SUGGEST_COPY.loadingButton : state.kind === 'result' ? SUGGEST_COPY.again : SUGGEST_COPY.button}
    </button>
  );
};

const P = {
  wrap: 'rounded-2xl border border-softBorder bg-white p-4 md:p-5',
  head: 'flex flex-wrap items-center justify-between gap-3 mb-3',
  title: 'font-display font-semibold text-base text-ink inline-flex items-center gap-1.5',
  note: 'text-xs text-inkMuted',
  issueChip: 'inline-flex items-center rounded-full bg-attention-surface text-attention-on text-xs font-bold px-2.5 py-0.5',
  colHead: 'text-[11px] font-bold uppercase tracking-wide text-inkMuted mb-1',
  cur: 'rounded-xl bg-cream border border-softBorder p-3 text-sm text-ink break-words',
  sug: 'rounded-xl bg-success-light border border-success/30 p-3 text-sm text-ink break-words',
  same: 'rounded-xl border border-dashed border-softBorder p-3 text-sm text-inkMuted',
} as const;

const Header: React.FC<{ extra?: React.ReactNode }> = ({ extra }) => (
  <div className={P.head}>
    <div>
      <h4 className={P.title}>
        <Sparkles className="w-4 h-4" aria-hidden="true" />
        {SUGGEST_COPY.title}
      </h4>
      <p className={P.note}>{SUGGEST_COPY.caption}</p>
    </div>
    {extra}
  </div>
);

const CompareRow: React.FC<{ label: string; current: React.ReactNode; suggested: React.ReactNode | null; testId: string }> = ({
  label,
  current,
  suggested,
  testId,
}) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={testId}>
    <div className="min-w-0">
      <p className={P.colHead}>
        {label} · {SUGGEST_COPY.now}
      </p>
      <div className={P.cur}>{current}</div>
    </div>
    <div className="min-w-0">
      <p className={P.colHead}>
        {label} · {SUGGEST_COPY.suggested}
      </p>
      {suggested == null ? <div className={P.same}>{SUGGEST_COPY.noChange}</div> : <div className={P.sug}>{suggested}</div>}
    </div>
  </div>
);

const OptionList: React.FC<{ options: string[]; correct: number | null }> = ({ options, correct }) => (
  <ol className="space-y-1">
    {options.map((o, i) => (
      <li key={i} className="flex gap-2">
        <span className="text-inkMuted w-4 shrink-0">{letter(i)}</span>
        <span className="min-w-0">
          {o}
          {i === correct && (
            <span className="ml-1 rounded-full bg-white text-success-on text-[11px] font-bold px-1.5 py-0.5 ring-1 ring-success/40">
              {SUGGEST_COPY.correct}
            </span>
          )}
        </span>
      </li>
    ))}
  </ol>
);

const Pills: React.FC<{ items: string[] }> = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((x) => (
      <span key={x} className="rounded-full bg-white ring-1 ring-softBorder text-ink text-xs font-bold px-2 py-0.5">
        {x}
      </span>
    ))}
  </div>
);

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(t);
  }, [copied]);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <button type="button" className={C.btnSecondary} onClick={onCopy} aria-live="polite" data-testid="suggest-copy">
      {copied ? <Check className="w-4 h-4" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
      {copied ? SUGGEST_COPY.copied : SUGGEST_COPY.copy}
    </button>
  );
};

export const SuggestPanel: React.FC<{
  state: State;
  question: QuestionContent;
  onRetry: () => void;
  timeZone?: string;
}> = ({ state, question, onRetry, timeZone }) => {
  if (state.kind === 'idle') return null;

  if (state.kind === 'loading') {
    return (
      <div className={P.wrap} aria-busy="true" role="status" data-testid="suggest-loading">
        <Header />
        <p className="text-sm text-inkMuted mb-3">{SUGGEST_COPY.loadingBody}</p>
        <div className="flex gap-2 mb-4">
          <div className="h-5 w-40 rounded-full bg-cream-dark animate-pulse" />
          <div className="h-5 w-32 rounded-full bg-cream-dark animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="h-16 rounded-xl bg-cream animate-pulse" />
          <div className="h-16 rounded-xl bg-cream-dark animate-pulse" />
        </div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <ErrorBanner
        testId="suggest-error"
        title={SUGGEST_COPY.error}
        detail={SUGGEST_COPY.errorDetail}
        onRetry={onRetry}
        retryLabel={SUGGEST_COPY.tryAgain}
      />
    );
  }

  if (state.kind === 'limited' || state.kind === 'notice') {
    const limited = state.kind === 'limited';
    return (
      <div role="status" className="rounded-2xl border border-softBorder bg-white p-3 text-sm text-ink flex items-start gap-3" data-testid={limited ? 'suggest-limited' : 'suggest-notice'}>
        {limited ? (
          <Clock className="w-4 h-4 mt-0.5 shrink-0 text-inkMuted" aria-hidden="true" />
        ) : (
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-inkMuted" aria-hidden="true" />
        )}
        <div className="flex-1 min-w-0">
          {limited ? (
            <>
              <p className="font-bold">{SUGGEST_COPY.limitedTitle}</p>
              <p className="text-xs text-inkMuted mt-0.5">{state.detail ?? SUGGEST_COPY.limitedFallback}</p>
            </>
          ) : (
            <p className="font-bold">{state.detail ?? SUGGEST_COPY.notEnoughFallback}</p>
          )}
        </div>
      </div>
    );
  }

  const s = state.data;
  const copyText = buildCopyText(question, s, new Date(), timeZone);
  const isFitb = question.question_type === 'fill_blank';

  if (s.no_change_recommended) {
    return (
      <div className={P.wrap} data-testid="suggest-result">
        <Header extra={<CopyButton text={copyText} />} />
        <div className="flex items-start gap-3 rounded-xl bg-cream p-3" data-testid="suggest-no-change">
          <Check className="w-5 h-5 text-success-on mt-0.5 shrink-0" aria-hidden="true" />
          <div>
            <p className="text-sm font-bold text-ink">{SUGGEST_COPY.noChangeTitle}</p>
            <p className="text-sm text-ink mt-0.5">{s.summary}</p>
          </div>
        </div>
      </div>
    );
  }

  const currentExpl = question.explanation || question.correct_explanation || '';
  return (
    <div className={P.wrap} data-testid="suggest-result">
      <Header extra={<CopyButton text={copyText} />} />
      <p className="text-sm text-ink font-semibold mb-2">{s.summary}</p>
      {s.issues.length > 0 && (
        <>
          <div className="flex flex-wrap gap-2 mb-1" data-testid="suggest-issues">
            {s.issues.map((i, idx) => (
              <span key={idx} className={P.issueChip}>
                {ISSUE_LABEL[i.category] ?? i.category}
              </span>
            ))}
          </div>
          <ul className="text-sm text-ink space-y-1 mb-4 list-disc pl-5">
            {s.issues.map((i, idx) => (
              <li key={idx}>
                <b>{ISSUE_LABEL[i.category] ?? i.category}:</b> {i.explanation}
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="space-y-3">
        <CompareRow label={SUGGEST_COPY.rowQuestion} current={question.question_text} suggested={s.suggested_question_text} testId="compare-question" />
        {isFitb ? (
          <CompareRow
            label={SUGGEST_COPY.rowAccepted}
            current={<Pills items={question.accepted_answers ?? []} />}
            suggested={s.suggested_accepted_answers ? <Pills items={s.suggested_accepted_answers} /> : null}
            testId="compare-accepted"
          />
        ) : (
          <CompareRow
            label={SUGGEST_COPY.rowOptions}
            current={<OptionList options={question.options ?? []} correct={question.correct_index} />}
            suggested={
              s.suggested_options ? (
                <OptionList options={s.suggested_options} correct={s.suggested_correct_index ?? question.correct_index} />
              ) : isAnswerKeyOnlyChange(question, s) ? (
                <OptionList options={question.options ?? []} correct={s.suggested_correct_index} />
              ) : null
            }
            testId="compare-options"
          />
        )}
        <CompareRow label={SUGGEST_COPY.rowExplanation} current={currentExpl} suggested={s.suggested_explanation} testId="compare-explanation" />
      </div>
      <p className={`${P.note} mt-3`}>{SUGGEST_COPY.copyHint}</p>
    </div>
  );
};
